import { buildClarityWorkspaceUrl } from './build-clarity-workspace-url.util';
import {
	readClarityWorkspaceRoute,
	toClaritySectionUrl,
	toNavigationUnlessCurrent,
} from './clarity-workspace-route.util';
import { CLARITY_DOCUMENTS_ROUTE_SEGMENT } from './constants/clarity-documents-route.constant';
import type { ClarityFileTarget } from './models/clarity-file-target.model';
import type { ClarityWorkspaceNavigation } from './models/clarity-workspace-navigation.model';
import { parseClarityWorkspaceLocation } from './parse-clarity-workspace-location.util';

/** Ported from Clarity `apps/web/src/shared/hooks/useWorkspaceRoute.ts` (commit `9015ce88c`). */
/**
 * Opening a Domain file — brain's `openFile(fileId, { replace, systemDomain })` (`:169–187`): a **push** unless the
 * target's `replace` is set, to the current domain with `file` set to `fileId`, that drops `doc`, `page`, `oi` and
 * `action`, carries every other query key verbatim, and is `null` — no navigation — when that URL is `currentUrl`
 * (their TR-014).
 *
 * The tab is the current one when the target is in the System Domain, where the tab is inert and the System Domain page
 * shows the file in place of its console; everywhere else it is `documents`.
 *
 * It builds the URL only. Closing a panel or recording the visit stays with the caller, as it does in brain's page.
 * Only the integration is adapted, as in the neighbouring workspace rules: the current URL is an argument read at call
 * time, the answer sits under `sectionPath`, the options travel inside the target, and the navigation is handed back
 * rather than performed.
 */
export function toClarityFileNavigation(
	currentUrl: string,
	sectionPath: string,
	target: ClarityFileTarget,
): ClarityWorkspaceNavigation | null {
	const current = readClarityWorkspaceRoute(currentUrl, sectionPath);
	const { domainId, tab } = parseClarityWorkspaceLocation(current.params, current.search);
	const url = buildClarityWorkspaceUrl(
		{
			domainId,
			tab: target.systemDomain ? tab : CLARITY_DOCUMENTS_ROUTE_SEGMENT,
			doc: null,
			page: null,
			file: target.fileId,
			oi: null,
			action: null,
		},
		current.search,
	);

	return toNavigationUnlessCurrent(current, toClaritySectionUrl(sectionPath, url), target.replace ?? false);
}
